import { useParams, Link } from 'react-router-dom';
import SiteLayout from '@/components/feature/SiteLayout';
import Breadcrumbs from '@/components/feature/Breadcrumbs';
import CaseCard from '@/components/feature/CaseCard';
import useSeo, { SITE_NAME, SITE_ORIGIN } from '@/hooks/useSeo';
import { cases, categories, hotRanking, evidenceLabelMap, type CaseItem } from '@/mocks/cases';
import EvidenceList from './components/EvidenceList';
import Timeline from './components/Timeline';

const statusStyle: Record<CaseItem['status'], string> = {
  已核实: 'bg-primary-100 text-primary-700',
  待回应: 'bg-accent-100 text-accent-900',
  审核中: 'bg-secondary-100 text-secondary-800',
};

export default function CaseDetail() {
  const { slug } = useParams<{ slug: string }>();
  const item = cases.find((c) => c.slug === slug);

  const category = categories.find((cat) => cat.id === item?.categoryId);
  const relatedItems = (item?.related ?? [])
    .map((id) => cases.find((c) => c.id === id))
    .filter((c): c is CaseItem => Boolean(c));

  useSeo({
    title: item ? `${item.title} | ${SITE_NAME}` : `未找到该爆料 | ${SITE_NAME}`,
    description: item
      ? `${item.summary} 材料类型包含${item.evidence.map((key) => evidenceLabelMap[key]).join('、')}，当前核实状态：${item.status}。`
      : '你访问的爆料页面不存在或已调整链接，请返回爆料列表继续浏览。',
    keywords: item ? ['网红爆料', ...item.tags].join(',') : '网红爆料,爆料列表',
    path: item ? `/case/${item.slug}` : '/cases',
    type: 'article',
    image: item?.cover,
    jsonLd: item
      ? [
          {
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: item.title,
            description: item.summary,
            image: item.cover,
            datePublished: item.publishDate,
            dateModified: item.publishDate,
            articleSection: category?.name,
            inLanguage: 'zh-CN',
            keywords: item.tags.join(','),
            author: { '@type': 'Organization', name: item.author },
            publisher: { '@type': 'Organization', name: SITE_NAME, url: SITE_ORIGIN },
            mainEntityOfPage: `${SITE_ORIGIN}/case/${item.slug}`,
          },
          {
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: '首页', item: SITE_ORIGIN },
              { '@type': 'ListItem', position: 2, name: '爆料列表', item: `${SITE_ORIGIN}/cases` },
              { '@type': 'ListItem', position: 3, name: item.title, item: `${SITE_ORIGIN}/case/${item.slug}` },
            ],
          },
        ]
      : undefined,
  });

  if (!item) {
    return (
      <SiteLayout>
        <section className="mx-auto flex max-w-[1280px] flex-col items-center px-4 py-24 text-center md:px-6">
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-3xl text-foreground-400">
            <i className="ri-file-unknow-line"></i>
          </span>
          <h1 className="mt-5 font-heading text-2xl font-bold text-foreground-950">没有找到这条爆料</h1>
          <p className="mt-3 max-w-md text-sm text-foreground-600">
            链接可能已经调整，或者该内容已被合并到其他条目。你可以回到爆料列表继续浏览。
          </p>
          <Link
            to="/cases"
            className="mt-6 rounded-md bg-primary-600 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-700"
          >
            返回爆料列表
          </Link>
        </section>
      </SiteLayout>
    );
  }

  return (
    <SiteLayout>
      <article>
        <header className="border-b border-background-200 bg-background-100">
          <div className="mx-auto max-w-[1280px] px-4 py-8 md:px-6 md:py-10">
            <Breadcrumbs
              items={[
                { label: '首页', to: '/' },
                { label: '爆料列表', to: '/cases' },
                { label: category?.name ?? '爆料详情', to: category ? `/cases?cat=${category.id}` : undefined },
                { label: item.title },
              ]}
            />
            <div className="mt-4 flex flex-wrap items-center gap-2">
              {category && (
                <Link
                  to={`/cases?cat=${category.id}`}
                  className="rounded-md bg-background-50 px-2.5 py-1 text-xs text-foreground-700 hover:text-primary-600"
                >
                  {category.name}
                </Link>
              )}
              <span className={`rounded-md px-2.5 py-1 text-xs ${statusStyle[item.status]}`}>
                <i className="ri-checkbox-circle-line mr-1"></i>
                {item.status}
              </span>
            </div>
            <h1 className="mt-4 max-w-4xl font-heading text-2xl font-black leading-snug text-foreground-950 md:text-4xl">
              {item.title}
            </h1>
            <p className="mt-4 max-w-3xl text-sm leading-relaxed text-foreground-600 md:text-base">{item.summary}</p>
            <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-foreground-500">
              <span className="flex items-center gap-1">
                <i className="ri-user-line"></i>
                {item.author}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-calendar-line"></i>
                {item.publishDate}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-time-line"></i>
                阅读约 {item.readTime}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-eye-line"></i>
                {item.views}
              </span>
            </div>
          </div>
        </header>

        <div className="mx-auto max-w-[1280px] px-4 py-10 md:px-6">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-12">
            <div className="lg:col-span-8">
              <div className="h-64 w-full overflow-hidden rounded-lg bg-background-200 md:h-96">
                <img src={item.cover} alt={item.coverAlt} className="h-full w-full object-cover object-top" />
              </div>
              <p className="mt-2 text-xs text-foreground-500">图：{item.coverAlt}（示意配图，非材料原件）</p>

              <section className="mt-8">
                <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">事件概述</h2>
                <div className="mt-4 space-y-4 text-sm leading-relaxed text-foreground-700 md:text-base">
                  <p>
                    围绕{item.tags.join('、')}等争议点，投稿人整理了可核对的材料并提交至本站。我们按时间顺序对材料做了编号，
                    并对关键时间点做了标注，方便逐项比对。
                  </p>
                  <p>
                    需要说明的是，以下材料仅代表投稿人提供的原始记录与陈述。当事方若已作出回应，我们会在核实状态与页面备注中同步更新，
                    不会删除原始内容，以便读者看到完整的沟通过程。
                  </p>
                </div>
              </section>

              <section className="mt-10">
                <div className="flex items-center justify-between">
                  <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">证据材料</h2>
                  <span className="text-xs text-foreground-500">共 {item.evidenceItems.length} 项</span>
                </div>
                <div className="mt-4">
                  <EvidenceList items={item.evidenceItems} />
                </div>
              </section>

              <section className="mt-10">
                <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">事件时间线</h2>
                <div className="mt-5">
                  <Timeline items={item.timeline} />
                </div>
              </section>

              <aside className="mt-8 rounded-lg border border-background-200 bg-background-100 p-5">
                <h2 className="flex items-center gap-2 font-heading text-base font-semibold text-foreground-950">
                  <i className="ri-error-warning-line text-primary-600"></i>
                  材料说明与异议处理
                </h2>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">
                  页面中的当事人均使用化名，证件号、手机号与银行账号已做遮挡。若你是事件相关方并希望补充说明或提交反证，
                  可发送邮件至 appeal@baoliao.example.com，我们会在核实后更新页面标注或作撤稿处理。
                </p>
              </aside>
            </div>

            <aside className="lg:col-span-4">
              <div className="sticky top-24 space-y-6">
                <div className="rounded-lg border border-background-200 bg-background-50 p-5">
                  <h2 className="font-heading text-base font-semibold text-foreground-950">本文要点</h2>
                  <ul className="mt-3 space-y-2 text-sm text-foreground-700">
                    <li className="flex gap-2">
                      <i className="ri-price-tag-3-line mt-0.5 text-primary-600"></i>
                      {item.tags.join('、')}
                    </li>
                    <li className="flex gap-2">
                      <i className="ri-stack-line mt-0.5 text-primary-600"></i>
                      证据形式：{item.evidence.map((key) => evidenceLabelMap[key]).join('、')}
                    </li>
                    <li className="flex gap-2">
                      <i className="ri-shield-check-line mt-0.5 text-primary-600"></i>
                      核实状态：{item.status}
                    </li>
                    <li className="flex gap-2">
                      <i className="ri-history-line mt-0.5 text-primary-600"></i>
                      时间跨度：共 {item.timeline.length} 个关键节点
                    </li>
                  </ul>
                </div>

                {relatedItems.length > 0 && (
                  <div className="rounded-lg border border-background-200 bg-background-50 p-5">
                    <h2 className="font-heading text-base font-semibold text-foreground-950">相关爆料</h2>
                    <ul className="mt-3 space-y-3">
                      {relatedItems.map((rel) => (
                        <li key={rel.id}>
                          <Link to={`/case/${rel.slug}`} className="text-sm leading-snug text-foreground-700 hover:text-primary-600">
                            {rel.title}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                <div className="rounded-lg border border-background-200 bg-background-50 p-5">
                  <h2 className="font-heading text-base font-semibold text-foreground-950">爆料热榜</h2>
                  <ol className="mt-3 space-y-3">
                    {hotRanking.slice(0, 5).map((hot, index) => {
                      const target = cases.find((c) => c.id === hot.id);
                      return (
                        <li key={hot.id}>
                          <Link to={`/case/${target?.slug ?? ''}`} className="flex items-start gap-2.5 group">
                            <span
                              className={`flex h-5 w-5 shrink-0 items-center justify-center rounded text-xs font-bold ${
                                index < 3 ? 'bg-primary-600 text-background-50' : 'bg-background-200 text-foreground-600'
                              }`}
                            >
                              {index + 1}
                            </span>
                            <span className="text-sm leading-snug text-foreground-700 group-hover:text-primary-600">
                              {hot.title}
                            </span>
                          </Link>
                        </li>
                      );
                    })}
                  </ol>
                </div>

                <div className="rounded-lg bg-primary-600 p-5">
                  <h2 className="font-heading text-base font-semibold text-background-50">有相关材料？</h2>
                  <p className="mt-2 text-sm leading-relaxed text-primary-100">
                    欢迎补充录音、录屏或凭证，我们会与现有材料一并核对。
                  </p>
                  <Link
                    to="/about#submit"
                    className="mt-4 inline-block whitespace-nowrap rounded-md bg-background-50 px-4 py-2 text-sm font-medium text-primary-700 transition-colors hover:bg-background-100"
                  >
                    提交材料
                  </Link>
                </div>
              </div>
            </aside>
          </div>

          {relatedItems.length > 0 && (
            <section className="mt-14 border-t border-background-200 pt-10">
              <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">继续阅读相关爆料</h2>
              <div className="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
                {relatedItems.map((rel) => (
                  <CaseCard key={rel.id} item={rel} />
                ))}
              </div>
            </section>
          )}
        </div>
      </article>
    </SiteLayout>
  );
}